import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Tooltip,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { customerService, purchaseService } from '../../services/financeService';
import './Financial.css';

const fmt = (val) =>
  val != null
    ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : '—';

const StatusChip = ({ cs }) => {
  if (cs.limitExpired) {
    return <Chip label="Limit Expired" sx={{ bgcolor: 'var(--color-accent-light)', color: 'var(--color-accent-hover)' }} size="small" icon={<WarningAmberIcon />} />;
  }
  if (cs.limitExceeded) {
    return <Chip label="Over Limit" color="error" size="small" icon={<ErrorIcon />} />;
  }
  if (cs.limitWarning) {
    return <Chip label="Near Limit" color="warning" size="small" icon={<WarningAmberIcon />} />;
  }
  if (cs.overdueInvoiceCount > 0) {
    return <Chip label="Overdue" sx={{ bgcolor: 'var(--color-warning-light)', color: 'var(--color-warning-strong)' }} size="small" icon={<WarningAmberIcon />} />;
  }
  return <Chip label="Healthy" color="success" size="small" icon={<CheckCircleIcon />} />;
};

const CreditCard = ({ cs }) => {
  const [expanded, setExpanded] = useState(false);

  const barColor = cs.limitExceeded
    ? 'error'
    : cs.limitWarning
    ? 'warning'
    : 'success';

  const usage = cs.usagePercent != null ? Math.min(cs.usagePercent, 100) : null;

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: cs.limitExceeded
          ? 'error.light'
          : cs.limitWarning
          ? 'warning.light'
          : 'var(--color-border-medium)',
        borderRadius: 2,
        mb: 2,
        overflow: 'hidden',
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          bgcolor: cs.limitExceeded
            ? 'error.50'
            : cs.limitWarning
            ? 'warning.50'
            : 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {cs.customerName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {cs.customerCode}
          </Typography>
        </Box>
        <StatusChip cs={cs} />
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Expired limit notice */}
      {cs.limitExpired && cs.creditLimit && (
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'var(--color-accent-hover)', fontStyle: 'italic' }}>
            Credit limit of PKR {fmt(cs.creditLimit)} expired on{' '}
            {new Date(cs.creditLimitValidUntil).toLocaleDateString()} — not enforced until renewed.
          </Typography>
        </Box>
      )}

      {/* Credit Bar */}
      {usage != null && (
        <Box sx={{ px: 2, pt: 1, pb: cs.limitExceeded || expanded ? 0 : 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Outstanding: <strong>PKR {fmt(cs.currentOutstanding)}</strong>
              {cs.usdOutstanding > 0 && cs.exchangeRateUsed && (
                <span style={{ fontSize: '0.75rem', opacity: 0.8 }}> (incl. USD {fmt(cs.usdOutstanding)} @ {Number(cs.exchangeRateUsed).toFixed(2)})</span>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Limit: <strong>PKR {fmt(cs.creditLimit)}</strong>
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={usage}
            color={barColor}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" color={cs.limitExceeded ? 'error.main' : 'text.secondary'}>
              {usage}% used
            </Typography>
            {cs.availableCredit != null && (
              <Typography variant="caption" color={cs.availableCredit < 0 ? 'error.main' : 'success.main'}>
                {cs.availableCredit >= 0
                  ? `PKR ${fmt(cs.availableCredit)} available`
                  : `PKR ${fmt(Math.abs(cs.availableCredit))} over limit`}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* No limit set — show outstanding only */}
      {usage == null && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Outstanding:{' '}
            <strong>PKR {fmt(cs.currentOutstanding)}</strong>
            {cs.usdOutstanding > 0 && cs.exchangeRateUsed
              ? <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                  {' '}= USD {fmt(cs.usdOutstanding)} × {Number(cs.exchangeRateUsed).toFixed(2)}
                </span>
              : <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}> (no credit limit set)</span>
            }
          </Typography>
        </Box>
      )}

      {/* USD Credit Limit row */}
      {cs.usdCreditLimit && (
        <Box sx={{ px: 2, py: 1, borderTop: '1px dashed var(--color-border-medium)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              USD Outstanding: <strong>USD {fmt(cs.usdOutstanding || 0)}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              USD Limit: <strong>USD {fmt(cs.usdCreditLimit)}</strong>
            </Typography>
          </Box>
          <Typography variant="caption" color={cs.usdLimitExceeded ? 'error.main' : 'success.main'}>
            {cs.usdLimitExceeded
              ? `USD OVER LIMIT by ${fmt(Math.abs(cs.usdAvailableCredit))}`
              : `USD ${fmt(cs.usdAvailableCredit)} available`}
          </Typography>
        </Box>
      )}

      {/* Expanded Detail */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          <Grid container spacing={2} sx={{ mb: cs.overdueInvoices?.length ? 2 : 0 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Open Invoices
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {cs.openInvoiceCount}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Overdue Invoices
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={cs.overdueInvoiceCount > 0 ? 'error.main' : 'text.primary'}
              >
                {cs.overdueInvoiceCount}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Overdue Amount
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={cs.overdueAmount > 0 ? 'error.main' : 'text.primary'}
              >
                PKR {fmt(cs.overdueAmount)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Oldest Overdue
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {cs.oldestOverdueDate
                  ? new Date(cs.oldestOverdueDate).toLocaleDateString()
                  : '—'}
              </Typography>
            </Grid>
          </Grid>

          {/* Overdue Invoice List */}
          {cs.overdueInvoices?.length > 0 && (
            <>
              <Typography variant="caption" fontWeight={600} color="error.main" sx={{ mb: 1, display: 'block' }}>
                Overdue Invoices
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid var(--color-error-light)', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'var(--color-error-light)' }}>
                      <TableCell sx={{ fontSize: '0.7rem', fontWeight: 700 }}>Invoice #</TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', fontWeight: 700 }}>Due Date</TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', fontWeight: 700 }}>Days Late</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>Balance Due</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cs.overdueInvoices.map((inv) => (
                      <TableRow key={inv.invoiceId} hover>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{inv.invoiceNumber}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          {new Date(inv.dueDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          <Chip
                            label={`${inv.daysOverdue}d`}
                            color={inv.daysOverdue > 30 ? 'error' : 'warning'}
                            size="small"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'error.main', fontWeight: 600 }}>
                          {inv.currency || 'PKR'} {fmt(inv.balanceDue)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

const SummaryCard = ({ label, value, color, icon }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      border: '1px solid var(--color-border-medium)',
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        bgcolor: `${color}.100`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { sx: { color: `${color}.600` } })}
    </Box>
    <Box>
      <Typography variant="h6" fontWeight={700} lineHeight={1}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Paper>
);

const VendorLedgerRow = ({ v }) => (
  <TableRow hover>
    <TableCell sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{v.vendorName}</TableCell>
    <TableCell align="right" sx={{ fontSize: '0.82rem' }}>PKR {fmt(v.totalPurchased)}</TableCell>
    <TableCell align="right" sx={{ fontSize: '0.82rem', color: 'success.main' }}>PKR {fmt(v.totalPaid)}</TableCell>
    <TableCell align="right" sx={{ fontSize: '0.82rem' }}>
      <Typography
        component="span"
        sx={{ fontWeight: 700, color: Number(v.outstanding) > 0 ? 'error.main' : 'text.secondary', fontSize: '0.82rem' }}
      >
        PKR {fmt(v.outstanding)}
      </Typography>
    </TableCell>
    <TableCell align="right" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{v.purchaseCount}</TableCell>
  </TableRow>
);

const CreditManagement = () => {
  const [tab, setTab] = useState('receivable'); // receivable | payable
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all | exceeded | warning | overdue
  const [vendors, setVendors] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customerService.getCreditAlerts();
      setAlerts(res.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    setVendorLoading(true);
    try {
      const data = await purchaseService.getVendorLedger();
      setVendors(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setVendorLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    fetchVendors();
  }, [fetchAlerts, fetchVendors]);

  // Summary stats (receivable)
  const exceeded = alerts.filter((a) => a.limitExceeded);
  const warning = alerts.filter((a) => !a.limitExceeded && a.limitWarning);
  const overdue = alerts.filter((a) => !a.limitExceeded && !a.limitWarning && a.overdueInvoiceCount > 0);
  const healthy = alerts.filter((a) => !a.limitExceeded && !a.limitWarning && a.overdueInvoiceCount === 0);

  const totalOutstanding = alerts.reduce(
    (sum, a) => sum + (Number(a.currentOutstanding) || 0),
    0
  );

  const filtered =
    filter === 'exceeded'
      ? exceeded
      : filter === 'warning'
      ? warning
      : filter === 'overdue'
      ? overdue
      : filter === 'healthy'
      ? healthy
      : alerts;

  // Vendor payable stats
  const totalPayable = vendors.reduce((s, v) => s + (Number(v.outstanding) || 0), 0);
  const vendorsDue = vendors.filter(v => Number(v.outstanding) > 0);
  const vendorsSorted = [...vendors].sort((a, b) => Number(b.outstanding) - Number(a.outstanding));

  return (
    <Box className="financial-container">
      {/* Header */}
      <Box className="page-header">
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Credit & Outstanding
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Customer receivables and vendor payables
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={() => { fetchAlerts(); fetchVendors(); }} disabled={loading || vendorLoading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Top-level tab toggle */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <Button
          variant={tab === 'receivable' ? 'contained' : 'outlined'}
          onClick={() => setTab('receivable')}
          sx={{ borderRadius: 4, textTransform: 'none', fontWeight: 600 }}
        >
          Customer Receivables
          <Box component="span" sx={{ ml: 1, background: tab === 'receivable' ? 'rgba(255,255,255,0.25)' : 'var(--color-border-medium)', borderRadius: 10, px: 1, fontSize: '0.75rem', fontWeight: 700 }}>
            PKR {fmt(totalOutstanding)}
          </Box>
        </Button>
        <Button
          variant={tab === 'payable' ? 'contained' : 'outlined'}
          color="error"
          onClick={() => setTab('payable')}
          sx={{ borderRadius: 4, textTransform: 'none', fontWeight: 600 }}
        >
          Purchase Payables
          <Box component="span" sx={{ ml: 1, background: tab === 'payable' ? 'rgba(255,255,255,0.25)' : 'var(--color-error-light)', borderRadius: 10, px: 1, fontSize: '0.75rem', fontWeight: 700, color: tab === 'payable' ? 'inherit' : 'var(--color-error)' }}>
            PKR {fmt(totalPayable)}
          </Box>
        </Button>
      </Box>

      {/* ── RECEIVABLE TAB ── */}
      {tab === 'receivable' && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <SummaryCard label="Over Credit Limit" value={exceeded.length} color="error" icon={<ErrorIcon />} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard label="Near Limit (≥80%)" value={warning.length} color="warning" icon={<WarningAmberIcon />} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard label="With Overdue" value={overdue.length} color="warning" icon={<WarningAmberIcon />} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard label="Total Outstanding" value={`PKR ${fmt(totalOutstanding)}`} color="primary" icon={<AttachMoneyIcon />} />
            </Grid>
          </Grid>

          {/* Filter Tabs */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {[
              { key: 'all', label: `All (${alerts.length})` },
              { key: 'exceeded', label: `Over Limit (${exceeded.length})` },
              { key: 'warning', label: `Near Limit (${warning.length})` },
              { key: 'overdue', label: `Overdue (${overdue.length})` },
              { key: 'healthy', label: `Healthy (${healthy.length})` },
            ].map((f) => (
              <Button key={f.key} size="small" variant={filter === f.key ? 'contained' : 'outlined'}
                onClick={() => setFilter(f.key)} sx={{ borderRadius: 4, textTransform: 'none', fontSize: '0.8rem' }}>
                {f.label}
              </Button>
            ))}
          </Box>

          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

          {!loading && filtered.length === 0 && (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed var(--color-border-strong)', borderRadius: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.light', mb: 1 }} />
              <Typography color="text.secondary">
                {filter === 'all' ? 'No customers with credit limits or outstanding balances found.' : `No customers in "${filter}" category.`}
              </Typography>
            </Paper>
          )}

          {!loading && filtered.map((cs) => <CreditCard key={cs.customerId} cs={cs} />)}

          {!loading && alerts.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Tip:</strong> Set or update credit limits for customers from the{' '}
              <strong>Customers</strong> page. Limits are automatically checked when creating new invoices.
            </Alert>
          )}
        </>
      )}

      {/* ── PAYABLE TAB ── */}
      {tab === 'payable' && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <SummaryCard label="Vendors with Balance" value={vendorsDue.length} color="error" icon={<ErrorIcon />} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard label="Total Vendors" value={vendors.length} color="primary" icon={<PersonIcon />} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard label="Total Purchased" value={`PKR ${fmt(vendors.reduce((s, v) => s + Number(v.totalPurchased || 0), 0))}`} color="warning" icon={<AttachMoneyIcon />} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard label="Total Payable" value={`PKR ${fmt(totalPayable)}`} color="error" icon={<AttachMoneyIcon />} />
            </Grid>
          </Grid>

          {vendorLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

          {!vendorLoading && vendors.length === 0 && (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed var(--color-border-strong)', borderRadius: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.light', mb: 1 }} />
              <Typography color="text.secondary">No purchase orders found.</Typography>
            </Paper>
          )}

          {!vendorLoading && vendors.length > 0 && (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid var(--color-border-medium)', borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: 'var(--color-neutral-50)' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Vendor</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Total Purchased</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Total Paid</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Outstanding</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>POs</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendorsSorted.map((v) => <VendorLedgerRow key={v.vendorId} v={v} />)}
                  <TableRow sx={{ background: 'var(--color-neutral-50)', borderTop: '2px solid var(--color-border-medium)' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem' }}>TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>PKR {fmt(vendors.reduce((s, v) => s + Number(v.totalPurchased || 0), 0))}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'success.main' }}>PKR {fmt(vendors.reduce((s, v) => s + Number(v.totalPaid || 0), 0))}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'error.main' }}>PKR {fmt(totalPayable)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>{vendors.reduce((s, v) => s + Number(v.purchaseCount || 0), 0)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default CreditManagement;
